import { ColumnDef, useTable } from '@tanstack/react-table';
import { dataTableFeatures, DataTableFeatures } from '@uag/react-core';
import Fuse from 'fuse.js';
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogueArticleTableBody } from 'app/modules/wallforming/components/catalogue-container/CatalogueArticleTableBody';
import { CatalogueArticleTableHeader } from 'app/modules/wallforming/components/catalogue-container/CatalogueArticleTableHeader';
import { getFilteredData } from 'app/modules/wallforming/components/catalogue-container/utils/articleFilterFunctions';
import { SearchInput } from 'app/modules/wallforming/components/search-field/SearchInput';
import { SettingTypeFilter } from 'app/modules/wallforming/components/setting-container/SettingTypeFilter';

import 'app/modules/wallforming/custom-styles/customCatalogueStyle.css';

import type { ArticleModel } from 'api/models';

interface CatalogueArticleTableProps {
    articleData: ArticleModel[] | undefined;
    showFilter?: boolean;
}

export const CatalogueArticleTable = ({ articleData, showFilter = true }: CatalogueArticleTableProps) => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState<string>('');

    const fuse = useMemo(() => {
        return articleData
            ? new Fuse(articleData, { keys: ['articleNumber', 'displayName'], threshold: 0.3, ignoreLocation: true })
            : null;
    }, [articleData]);

    const filteredData = useMemo(() => {
        return articleData ? getFilteredData(articleData, searchValue, fuse) : [];
    }, [articleData, searchValue, fuse]);

    const columns: ColumnDef<DataTableFeatures, ArticleModel>[] = useMemo(
        () => [
            {
                accessorKey: 'articleNumber',
                header: () => (
                    <div className="flex align-middle h-full justify-start text-[12.5px] pl-1 whitespace-nowrap overflow-visible text-ellipsis">
                        {t('labelArticleNumber')}
                    </div>
                ),
                cell: (info) => (
                    <div className="max-w-24 min-w-24 flex align-middle h-full justify-start pl-1">
                        {String(info.getValue())}
                    </div>
                )
            },
            {
                accessorKey: 'lifeCycleCode',
                header: () => (
                    <div className="flex align-middle h-full justify-start text-[12.5px] pl-1.5 whitespace-nowrap overflow-visible text-ellipsis">
                        {'LC'}
                    </div>
                ),
                cell: (info) => (
                    <div className="max-w-20 min-w-20 flex align-middle h-full justify-start pl-1">
                        {String(info.getValue())}
                    </div>
                )
            },
            {
                accessorKey: 'displayName',
                header: () => (
                    <div className="flex align-middle justify-start pl-1 text-[12.5px]">{t('labelArticleName')}</div>
                ),
                cell: (info) => (
                    <div className="max-w-24 min-w-24 flex align-middle justify-start pl-1">
                        {String(info.getValue())}
                    </div>
                )
            }
        ],
        [t]
    );

    const articleTable = useTable({
        key: 'article-table',
        features: dataTableFeatures,
        columns,
        data: filteredData,
        state: { globalFilter: searchValue },
        manualPagination: true
    });

    const handleOnPointerLeave = useCallback((): void => {
        document.body.style.cursor = 'auto';
    }, []);

    return (
        <div
            className="flex flex-col h-full w-full justify-items-stretch align-middle p-3 sm:p-0 overflow-auto"
            onPointerLeave={handleOnPointerLeave}
        >
            <div className="flex w-full">
                <div className="flex justify-items-end justify-end align-middle w-full">
                    <div className="w-2/3 h-auto min-h-7">
                        <SearchInput
                            placeholder={t('labelSearchPlaceholder')}
                            value={searchValue}
                            onChange={setSearchValue}
                        />
                    </div>
                    {showFilter && <SettingTypeFilter />}
                </div>
            </div>

            <div className="flex-1 overflow-auto h-full w-full justify-items-stretch mt-4 pr-0 mr-0">
                <table className="border-collapse text-sm pr-0 table-auto overflow-auto w-full">
                    <CatalogueArticleTableHeader articleTableHeader={articleTable.getHeaderGroups()} />
                    <CatalogueArticleTableBody articleTableRows={articleTable.getRowModel().rows} columns={columns} />
                </table>
            </div>
        </div>
    );
};
